import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { SearchHelper } from 'src/app/class/SearchHelper/SearchHelper.class';

const NATURAL_ORDER: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };
const NUMERIC_TERM = /^\d+$/;
const HAS_DIGIT = /\d/;

interface IndexedItem {
  item: any;
  text: string;
  normalizedText: string;
  code: string;
  haystack: string;
}

@Component({
  selector: 'app-select-search',
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectSearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: any[] = [];
  @Input() labelKey = 'label';
  @Input() textKey?: string;
  @Input() codeKey?: string;
  @Input() placeholder = '';
  @Input() noResultsLabel = '';
  @Input() debounceMs = 300;

  @ViewChild('input') inputRef?: ElementRef<HTMLInputElement>;

  term = '';
  orderedItems: any[] = [];
  noResults = false;

  private tokens: string[] = [];
  private normalizedTerm = '';
  private sortedIndex: IndexedItem[] = [];
  private indexByItem = new Map<any, IndexedItem>();
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private subscription?: Subscription;

  constructor(@Optional() private select: MatSelect, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.subscription = this.select?.openedChange.subscribe(opened => {
      if (opened) {
        setTimeout(() => this.inputRef?.nativeElement.focus());
      } else {
        this.clear();
      }
    });
  }

  ngOnChanges(): void {
    const index = this.items.map(item => this.toIndexed(item));
    this.indexByItem = new Map(index.map(entry => [entry.item, entry]));
    this.sortedIndex = index.sort((a, b) => a.text.localeCompare(b.text, 'pt-BR', NATURAL_ORDER));
    this.reorder();
  }

  ngOnDestroy(): void {
    clearTimeout(this.debounceTimer);
    this.subscription?.unsubscribe();
  }

  onInput(value: string): void {
    this.term = value;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.applyTerm(value), this.debounceMs);
  }

  clear(): void {
    clearTimeout(this.debounceTimer);
    this.term = '';
    this.applyTerm('');
  }

  // Plain label search (no code): every word must appear, any order, accents and case ignored.
  matches(label: string | null | undefined): boolean {
    const normalized = this.normalize(label ?? '');
    return this.tokens.every(token => normalized.includes(token));
  }

  // Options are hidden, not removed, so multi-select values outside the filter survive.
  matchesItem(item: any): boolean {
    const entry = this.indexByItem.get(item);
    return !entry || this.entryMatches(entry);
  }

  private applyTerm(value: string): void {
    this.normalizedTerm = this.normalize(value);
    this.tokens = this.normalizedTerm.split(/\s+/).filter(Boolean);
    this.reorder();
    this.cdr.markForCheck();
  }

  // A word with a digit may be in the code or in the text; a word with only letters must be in the text.
  private entryMatches(entry: IndexedItem): boolean {
    return this.tokens.every(token => (HAS_DIGIT.test(token) ? entry.haystack : entry.normalizedText).includes(token));
  }

  private reorder(): void {
    this.noResults = false;
    if (!this.tokens.length) {
      this.orderedItems = this.sortedIndex.map(entry => entry.item);
      return;
    }

    const visible: IndexedItem[] = [];
    const hidden: IndexedItem[] = [];
    this.sortedIndex.forEach(entry => (this.entryMatches(entry) ? visible : hidden).push(entry));
    this.noResults = !visible.length;

    this.orderedItems = [...this.rankMatches(visible), ...hidden].map(entry => entry.item);
  }

  private rankMatches(matches: IndexedItem[]): IndexedItem[] {
    if (NUMERIC_TERM.test(this.normalizedTerm)) {
      const byCode = (a: IndexedItem, b: IndexedItem) => a.code.localeCompare(b.code, 'pt-BR', NATURAL_ORDER);
      const inCode: IndexedItem[] = [];
      const others: IndexedItem[] = [];
      matches.forEach(entry => (entry.code.includes(this.normalizedTerm) ? inCode : others).push(entry));
      return [...inCode.sort(byCode), ...others];
    }

    // A name that starts with the term beats one that only contains it (the sort is stable, so text order stays).
    const startsWithTerm = (entry: IndexedItem) => (entry.normalizedText.startsWith(this.normalizedTerm) ? 0 : 1);
    return [...matches].sort((a, b) => startsWithTerm(a) - startsWithTerm(b));
  }

  private toIndexed(item: any): IndexedItem {
    const text = String(item?.[this.textKey ?? this.labelKey] ?? '');
    const code = this.codeKey ? this.normalize(String(item?.[this.codeKey] ?? '')) : '';
    const normalizedText = this.normalize(text);
    return { item, text, normalizedText, code, haystack: `${code} ${normalizedText}` };
  }

  private normalize(value: string): string {
    return SearchHelper.normalize(value);
  }
}
